const TOKEN_KEY = 'chatbot_access_token';

// Deliberately a plain module, not an Angular injectable: the HTTP
// interceptor needs the token on every request, including the one
// AuthService itself fires from its own constructor on a fresh page load.
// Injecting AuthService there would call inject(AuthService) while its own
// factory is still on the stack, which Angular's DI reports as NG0200
// (circular dependency) — reading storage directly sidesteps DI entirely.
// Prerendering runs this module in Node, where Web Storage doesn't exist —
// treat that as "no stored session" instead of crashing the render.
//
// A `typeof localStorage !== 'undefined'` check is NOT enough: Angular's
// prerender environment defines a `localStorage` global that is not a real
// Storage, so the typeof guard passes and the first `.getItem` throws
// "localStorage.getItem is not a function". That exception escapes
// AuthService's constructor, which aborts the render of every route — the
// whole marketing surface then ships as an empty <app-root> shell with no
// h1, no copy and no JSON-LD. Probe the method, not the object.
function storageAvailable(): boolean {
  try {
    return typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function';
  } catch {
    // Accessing localStorage throws outright in some privacy modes.
    return false;
  }
}

export function getStoredToken(): string | null {
  if (!storageAvailable()) {
    return null;
  }
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string): void {
  if (storageAvailable()) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Quota or privacy-mode failure — the in-memory session still works.
    }
  }
}

export function clearStoredToken(): void {
  if (storageAvailable()) {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Nothing to recover from; the token is already unusable.
    }
  }
}
